// Unified pixel-themed icon system for the entire game
// All icons are designed to match the game's aesthetic with consistent styling

interface PixelIcon {
  viewBox: string;
  path: string;
}

const pixelIcons = {
  // Navigation & UI Icons
  play: {
    viewBox: '0 0 24 24',
    path: `<path d="M6 4v16l12-8z" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linejoin="miter"/>`,
  },

  close: {
    viewBox: '0 0 24 24',
    path: `<path d="M6 6h2v2H6V6zm2 2h2v2H8V8zm2 2h2v2h-2v-2zm2 2h2v2h-2v-2zm2 2h2v2h-2v-2z" fill="currentColor"/>
          <path d="M16 6h2v2h-2V6zm-2 2h2v2h-2V8zm-2 2h2v2h-2v-2zm-2 2h2v2h-2v-2zm-2 2h2v2H8v-2z" fill="currentColor"/>`,
  },

  home: {
    viewBox: '0 0 24 24',
    path: `<path d="M10 2h4v2h-4V2zm-2 2h2v2H8V4zm8 0h2v2h-2V4zm-8 2H6v2h2V6zm8 0h2v2h-2V6z" fill="currentColor"/>
          <rect x="4" y="8" width="16" height="12" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="10" y="14" width="4" height="6" fill="currentColor"/>`,
  },

  settings: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="10" y="2" width="4" height="4" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="4" fill="currentColor"/>
          <rect x="2" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="18" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="4" y="4" width="4" height="4" fill="currentColor" opacity="0.7"/>
          <rect x="16" y="4" width="4" height="4" fill="currentColor" opacity="0.7"/>
          <rect x="4" y="16" width="4" height="4" fill="currentColor" opacity="0.7"/>
          <rect x="16" y="16" width="4" height="4" fill="currentColor" opacity="0.7"/>`,
  },

  // Game Action Icons
  restart: {
    viewBox: '0 0 24 24',
    path: `<path d="M4 10h2v4H4v-4zm2-2h2v2H6V8zm2-2h2v2H8V6zm2-2h2v2h-2V4zm2 0h2v2h-2V4zm2 2h2v2h-2V6zm2 2h2v2h-2V8zm2 2h2v4h-2v-4zm-2 4h2v2h-2v-2zm-2 2h2v2h-2v-2zm-2 2h2v2h-2v-2zm-2 0h2v2h-2v-2zm-2-2h2v2H8v-2zm-2-2h2v2H6v-2z" fill="none" stroke="currentColor" stroke-width="0"/>
          <rect x="4" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="6" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="14" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="16" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="2" height="2" fill="currentColor"/>
          <path d="M12 2h2v2h2v2h-4V2z" fill="currentColor"/>`,
  },

  fullscreen: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="8" height="2" fill="currentColor"/>
          <rect x="2" y="4" width="2" height="6" fill="currentColor"/>
          <rect x="14" y="2" width="8" height="2" fill="currentColor"/>
          <rect x="20" y="4" width="2" height="6" fill="currentColor"/>
          <rect x="2" y="14" width="2" height="6" fill="currentColor"/>
          <rect x="2" y="20" width="8" height="2" fill="currentColor"/>
          <rect x="14" y="20" width="8" height="2" fill="currentColor"/>
          <rect x="20" y="14" width="2" height="6" fill="currentColor"/>`,
  },

  // Shop & Currency Icons
  shop: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="8" width="16" height="12" fill="none" stroke="currentColor" stroke-width="2"/>
          <path d="M4 8h2V6h2V4h2V2h4v2h2v2h2v2h2" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="miter"/>
          <rect x="8" y="12" width="2" height="8" fill="currentColor"/>
          <rect x="14" y="12" width="2" height="8" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="4" fill="currentColor"/>`,
  },

  coin: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="4" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="18" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="6" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="12" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="14" width="2" height="2" fill="currentColor"/>`,
  },

  coins: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="8" width="12" height="8" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="11" y="11" width="2" height="2" fill="white"/>`,
  },

  gem: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="2" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="4" width="2" height="4" fill="currentColor"/>
          <rect x="16" y="4" width="2" height="4" fill="currentColor"/>
          <rect x="4" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="6" width="4" height="2" fill="white" opacity="0.6"/>
          <rect x="9" y="10" width="2" height="2" fill="white" opacity="0.4"/>
          <rect x="13" y="10" width="2" height="2" fill="white" opacity="0.4"/>`,
  },

  gems: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="10" y="12" width="4" height="8" fill="currentColor"/>
          <rect x="11" y="14" width="2" height="2" fill="white" opacity="0.8"/>`,
  },

  life: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="4" height="4" fill="currentColor"/>
          <rect x="14" y="4" width="4" height="4" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="10" y="6" width="4" height="2" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="2" y="8" width="2" height="4" fill="currentColor"/>
          <rect x="20" y="8" width="2" height="4" fill="currentColor"/>
          <rect x="4" y="12" width="16" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="2" fill="white" opacity="0.8"/>
          <rect x="12" y="8" width="2" height="2" fill="white" opacity="0.8"/>`,
  },

  heart: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="6" width="6" height="4" fill="currentColor"/>
          <rect x="14" y="6" width="6" height="4" fill="currentColor"/>
          <rect x="2" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="16" height="2" fill="currentColor"/>
          <rect x="6" y="12" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="14" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="16" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="18" width="2" height="2" fill="currentColor"/>`,
  },

  gift: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="8" width="16" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="16" height="10" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="10" fill="white" opacity="0.3"/>
          <rect x="10" y="2" width="4" height="6" fill="currentColor"/>
          <rect x="8" y="4" width="2" height="4" fill="currentColor"/>
          <rect x="14" y="4" width="2" height="4" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="2" y="8" width="20" height="4" fill="currentColor"/>
          <rect x="11" y="8" width="2" height="4" fill="white" opacity="0.4"/>`,
  },

  fire: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="9" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="13" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="7" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="15" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="12" width="4" height="2" fill="currentColor"/>
          <rect x="16" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="12" height="2" fill="currentColor"/>
          <rect x="7" y="16" width="10" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="12" width="2" height="4" fill="#f39c12" opacity="0.8"/>
          <rect x="9" y="14" width="2" height="2" fill="#e74c3c" opacity="0.6"/>
          <rect x="13" y="14" width="2" height="2" fill="#e74c3c" opacity="0.6"/>`,
  },

  score: {
    viewBox: '0 0 24 24',
    path: `<rect x="11" y="2" width="2" height="6" fill="currentColor"/>
          <rect x="9" y="6" width="6" height="2" fill="currentColor"/>
          <rect x="6" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="16" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="7" y="12" width="10" height="2" fill="currentColor"/>
          <rect x="8" y="14" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="2" fill="white" opacity="0.8"/>`,
  },

  // Level & Progress Icons
  star: {
    viewBox: '0 0 24 24',
    path: `<rect x="11" y="2" width="2" height="4" fill="currentColor"/>
          <rect x="9" y="6" width="6" height="2" fill="currentColor"/>
          <rect x="5" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="17" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="3" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="17" y="10" width="4" height="2" fill="currentColor"/>
          <rect x="7" y="12" width="10" height="2" fill="currentColor"/>
          <rect x="8" y="14" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>`,
  },

  lock: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="12" width="12" height="8" fill="currentColor" stroke="currentColor" stroke-width="2"/>
          <rect x="8" y="8" width="2" height="4" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="14" y="8" width="2" height="4" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="8" y="6" width="8" height="2" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="11" y="15" width="2" height="2" fill="white"/>`,
  },

  levelSelect: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="2" y="14" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="14" width="8" height="8" fill="currentColor" opacity="0.5"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  // Audio & Settings Icons
  sound: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="6" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="2" height="16" fill="currentColor"/>
          <rect x="14" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="16" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="20" y="4" width="2" height="16" fill="currentColor"/>`,
  },

  vibration: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="4" width="8" height="16" fill="currentColor" stroke="currentColor" stroke-width="2"/>
          <rect x="4" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="18" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="2" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="22" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="2" fill="white"/>
          <rect x="10" y="12" width="4" height="2" fill="white"/>
          <rect x="10" y="16" width="4" height="2" fill="white"/>`,
  },

  // Powerup & Item Icons
  speedBoost: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="6" width="4" height="2" fill="currentColor"/>
          <rect x="4" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="2" fill="currentColor"/>
          <rect x="2" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="10" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="12" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="12" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  shield: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="8" width="16" height="8" fill="currentColor"/>
          <rect x="6" y="16" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="6" fill="white"/>
          <rect x="9" y="12" width="6" height="2" fill="white"/>`,
  },

  hint: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="12" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="16" width="2" height="1" fill="currentColor"/>`,
  },

  invincibility: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="11" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="9" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="13" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="7" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="15" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="9" y="12" width="6" height="2" fill="currentColor"/>
          <rect x="11" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="16" width="4" height="1" fill="currentColor"/>`,
  },

  // Navigation Icons (missing from config)
  levels: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="2" y="14" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="14" width="8" height="8" fill="currentColor"/>
          <rect x="4" y="4" width="4" height="4" fill="white" opacity="0.8"/>
          <rect x="16" y="4" width="4" height="4" fill="white" opacity="0.8"/>
          <rect x="4" y="16" width="4" height="4" fill="white" opacity="0.8"/>
          <rect x="16" y="16" width="4" height="4" fill="white" opacity="0.8"/>`,
  },

  user: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="4" width="8" height="6" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="6" width="4" height="2" fill="white"/>
          <rect x="4" y="12" width="16" height="8" fill="currentColor"/>
          <rect x="6" y="14" width="12" height="4" fill="white" opacity="0.8"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>`,
  },

  continue: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="8" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="10" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="14" y="8" width="2" height="8" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="4" width="2" height="16" fill="currentColor"/>`,
  },

  pause: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="4" height="16" fill="currentColor"/>
          <rect x="14" y="4" width="4" height="16" fill="currentColor"/>`,
  },

  // Additional icons for completeness
  lightning: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="6" width="4" height="2" fill="currentColor"/>
          <rect x="4" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="2" fill="currentColor"/>
          <rect x="2" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="10" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="12" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="12" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  'arrow-left': {
    viewBox: '0 0 24 24',
    path: `<rect x="12" y="10" width="8" height="4" fill="currentColor"/>
          <rect x="8" y="8" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="2" y="10" width="2" height="4" fill="currentColor"/>`,
  },

  gamepad: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="8" width="12" height="8" fill="currentColor"/>
          <rect x="4" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="2" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="10" width="2" height="2" fill="white"/>
          <rect x="14" y="10" width="2" height="2" fill="white"/>
          <rect x="10" y="11" width="4" height="2" fill="white"/>
          <rect x="11" y="9" width="2" height="4" fill="white"/>`,
  },

  eye: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="10" width="12" height="4" fill="currentColor"/>
          <rect x="4" y="11" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="11" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="14" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="11" width="4" height="2" fill="white"/>
          <rect x="11" y="11" width="2" height="2" fill="currentColor"/>`,
  },

  // Additional icons needed for modals
  success: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="8" width="2" height="2" fill="currentColor"/>`,
  },

  retry: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="6" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="14" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="16" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="14" width="2" height="2" fill="currentColor"/>
          <path d="M12 2h2v2h2v2h-4V2z" fill="currentColor"/>`,
  },

  steps: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="18" width="4" height="2" fill="currentColor"/>
          <rect x="12" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  clock: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="11" y="8" width="2" height="6" fill="currentColor"/>
          <rect x="13" y="11" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="6" width="2" height="2" fill="currentColor"/>`,
  },

  speed: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="2" height="2" fill="currentColor"/>
          <rect x="4" y="4" width="2" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="14" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  // Additional icons for settings menu
  effects: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="4" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="6" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="8" width="16" height="8" fill="currentColor"/>
          <rect x="6" y="16" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="20" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="6" fill="white"/>
          <rect x="9" y="12" width="6" height="2" fill="white"/>`,
  },

  // Character customization icons
  mask: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="8" width="12" height="8" fill="currentColor"/>
          <rect x="4" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="8" y="10" width="2" height="2" fill="white"/>
          <rect x="14" y="10" width="2" height="2" fill="white"/>
          <rect x="10" y="13" width="4" height="2" fill="white"/>`,
  },

  crown: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="12" width="16" height="6" fill="currentColor"/>
          <rect x="6" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="11" y="8" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="10" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="14" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="4" fill="currentColor"/>
          <rect x="11" y="14" width="2" height="2" fill="#f39c12"/>
          <rect x="7" y="14" width="2" height="2" fill="#f39c12"/>
          <rect x="15" y="14" width="2" height="2" fill="#f39c12"/>`,
  },

  glasses: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="10" width="6" height="4" fill="currentColor"/>
          <rect x="14" y="10" width="6" height="4" fill="currentColor"/>
          <rect x="10" y="11" width="4" height="2" fill="currentColor"/>
          <rect x="6" y="12" width="2" height="2" fill="white"/>
          <rect x="16" y="12" width="2" height="2" fill="white"/>
          <rect x="2" y="11" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="11" width="2" height="2" fill="currentColor"/>`,
  },

  hair: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="2" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="16" height="2" fill="currentColor"/>
          <rect x="6" y="8" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="10" width="8" height="2" fill="currentColor"/>
          <rect x="2" y="8" width="2" height="4" fill="currentColor"/>
          <rect x="20" y="8" width="2" height="4" fill="currentColor"/>`,
  },

  hand: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="4" width="2" height="8" fill="currentColor"/>
          <rect x="10" y="2" width="2" height="10" fill="currentColor"/>
          <rect x="12" y="4" width="2" height="8" fill="currentColor"/>
          <rect x="14" y="6" width="2" height="6" fill="currentColor"/>
          <rect x="6" y="12" width="12" height="6" fill="currentColor"/>
          <rect x="8" y="18" width="8" height="2" fill="currentColor"/>`,
  },

  backpack: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="8" width="12" height="12" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="8" y="12" width="8" height="6" fill="white" opacity="0.3"/>
          <rect x="10" y="14" width="4" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="2" height="6" fill="currentColor"/>
          <rect x="18" y="10" width="2" height="6" fill="currentColor"/>`,
  },

  target: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="8" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="4" fill="white"/>
          <rect x="11" y="11" width="2" height="2" fill="currentColor"/>`,
  },

  trophy: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="4" width="8" height="8" fill="currentColor"/>
          <rect x="6" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="4" fill="currentColor"/>
          <rect x="10" y="12" width="4" height="4" fill="currentColor"/>
          <rect x="8" y="16" width="8" height="2" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="2" fill="#f39c12"/>
          <rect x="11" y="6" width="2" height="2" fill="#f39c12"/>`,
  },

  chart: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="18" width="2" height="4" fill="currentColor"/>
          <rect x="8" y="14" width="2" height="8" fill="currentColor"/>
          <rect x="12" y="10" width="2" height="12" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="16" fill="currentColor"/>
          <rect x="20" y="4" width="2" height="18" fill="currentColor"/>
          <rect x="2" y="20" width="20" height="2" fill="currentColor"/>`,
  },

  strength: {
    viewBox: '0 0 24 24',
    path: `<rect x="8" y="8" width="8" height="8" fill="currentColor"/>
          <rect x="6" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="16" y="10" width="2" height="4" fill="currentColor"/>
          <rect x="4" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="12" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="4" fill="white"/>
          <rect x="11" y="11" width="2" height="2" fill="currentColor"/>`,
  },

  loading: {
    viewBox: '0 0 24 24',
    path: `<rect x="10" y="2" width="4" height="6" fill="currentColor"/>
          <rect x="16" y="4" width="4" height="4" fill="currentColor" opacity="0.8"/>
          <rect x="18" y="10" width="4" height="4" fill="currentColor" opacity="0.6"/>
          <rect x="16" y="16" width="4" height="4" fill="currentColor" opacity="0.4"/>
          <rect x="10" y="18" width="4" height="4" fill="currentColor" opacity="0.2"/>
          <rect x="4" y="16" width="4" height="4" fill="currentColor" opacity="0.1"/>
          <rect x="2" y="10" width="4" height="4" fill="currentColor" opacity="0.05"/>
          <rect x="4" y="4" width="4" height="4" fill="currentColor" opacity="0.9"/>`,
  },

  wifi: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="12" width="20" height="2" fill="currentColor"/>
          <rect x="4" y="10" width="16" height="2" fill="currentColor"/>
          <rect x="6" y="8" width="12" height="2" fill="currentColor"/>
          <rect x="8" y="6" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="4" width="4" height="2" fill="currentColor"/>
          <rect x="11" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="10" y="16" width="4" height="2" fill="currentColor"/>`,
  },

  mobile: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="2" width="12" height="20" fill="currentColor"/>
          <rect x="8" y="4" width="8" height="14" fill="white"/>
          <rect x="10" y="19" width="4" height="2" fill="white"/>
          <rect x="11" y="6" width="2" height="2" fill="currentColor"/>`,
  },

  grid: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="2" width="8" height="8" fill="currentColor"/>
          <rect x="2" y="14" width="8" height="8" fill="currentColor"/>
          <rect x="14" y="14" width="8" height="8" fill="currentColor"/>`,
  },

  search: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="8" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="8" fill="currentColor"/>
          <rect x="14" y="6" width="2" height="8" fill="currentColor"/>
          <rect x="6" y="14" width="8" height="2" fill="currentColor"/>
          <rect x="14" y="14" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="18" y="18" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="20" width="2" height="2" fill="currentColor"/>`,
  },

  error: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="2" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="12" fill="currentColor"/>
          <rect x="6" y="18" width="12" height="2" fill="currentColor"/>
          <rect x="10" y="8" width="4" height="6" fill="currentColor"/>
          <rect x="11" y="16" width="2" height="2" fill="currentColor"/>`,
  },

  construction: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="18" width="20" height="4" fill="currentColor"/>
          <rect x="4" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="12" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="16" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="20" y="16" width="2" height="2" fill="currentColor"/>
          <rect x="8" y="8" width="8" height="8" fill="currentColor"/>
          <rect x="10" y="10" width="4" height="4" fill="white"/>
          <rect x="6" y="6" width="2" height="10" fill="currentColor"/>
          <rect x="16" y="6" width="2" height="10" fill="currentColor"/>`,
  },

  palette: {
    viewBox: '0 0 24 24',
    path: `<rect x="6" y="4" width="12" height="14" fill="currentColor"/>
          <rect x="4" y="6" width="2" height="10" fill="currentColor"/>
          <rect x="18" y="6" width="2" height="10" fill="currentColor"/>
          <rect x="8" y="8" width="2" height="2" fill="#e74c3c"/>
          <rect x="12" y="8" width="2" height="2" fill="#3498db"/>
          <rect x="16" y="8" width="2" height="2" fill="#f39c12"/>
          <rect x="8" y="12" width="2" height="2" fill="#27ae60"/>
          <rect x="12" y="12" width="2" height="2" fill="#9b59b6"/>
          <rect x="16" y="12" width="2" height="2" fill="#e67e22"/>`,
  },

  magnet: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="4" width="4" height="12" fill="currentColor"/>
          <rect x="16" y="4" width="4" height="12" fill="currentColor"/>
          <rect x="6" y="14" width="12" height="4" fill="currentColor"/>
          <rect x="8" y="12" width="8" height="2" fill="currentColor"/>
          <rect x="4" y="4" width="4" height="4" fill="currentColor" opacity="0.5"/>
          <rect x="16" y="4" width="4" height="4" fill="currentColor" opacity="0.5"/>
          <rect x="6" y="20" width="2" height="2" fill="currentColor" opacity="0.6"/>
          <rect x="11" y="21" width="2" height="2" fill="currentColor" opacity="0.6"/>
          <rect x="16" y="20" width="2" height="2" fill="currentColor" opacity="0.6"/>`,
  },

  doubleCoin: {
    viewBox: '0 0 24 24',
    path: `<rect x="3" y="7" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="6" y="10" width="4" height="4" fill="currentColor"/>
          <rect x="11" y="5" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="14" y="8" width="4" height="4" fill="currentColor"/>
          <rect x="7" y="19" width="10" height="2" fill="currentColor" opacity="0.5"/>`,
  },

  skip: {
    viewBox: '0 0 24 24',
    path: `<path d="M4 4v16l8-8z" fill="currentColor"/>
          <path d="M12 4v16l8-8z" fill="currentColor"/>
          <rect x="20" y="4" width="2" height="16" fill="currentColor"/>`,
  },

  infinity: {
    viewBox: '0 0 24 24',
    path: `<rect x="2" y="8" width="8" height="8" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="14" y="8" width="8" height="8" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="10" y="11" width="4" height="2" fill="currentColor"/>`,
  },

  calendar: {
    viewBox: '0 0 24 24',
    path: `<rect x="3" y="5" width="18" height="16" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="3" y="5" width="18" height="4" fill="currentColor"/>
          <rect x="7" y="2" width="2" height="5" fill="currentColor"/>
          <rect x="15" y="2" width="2" height="5" fill="currentColor"/>
          <rect x="7" y="12" width="3" height="3" fill="currentColor"/>
          <rect x="12" y="12" width="3" height="3" fill="currentColor"/>
          <rect x="7" y="16" width="3" height="3" fill="currentColor"/>
          <rect x="12" y="16" width="3" height="3" fill="currentColor" opacity="0.5"/>`,
  },

  book: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="3" width="16" height="18" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="4" y="3" width="4" height="18" fill="currentColor"/>
          <rect x="10" y="7" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="11" width="8" height="2" fill="currentColor"/>
          <rect x="10" y="15" width="5" height="2" fill="currentColor"/>`,
  },

  vip: {
    viewBox: '0 0 24 24',
    path: `<path d="M3 8l4 4 5-8 5 8 4-4v10H3z" fill="currentColor"/>
          <rect x="3" y="19" width="18" height="2" fill="currentColor"/>
          <rect x="11" y="12" width="2" height="3" fill="white" opacity="0.7"/>`,
  },

  wizardHat: {
    viewBox: '0 0 24 24',
    path: `<path d="M12 2l4 12H8z" fill="currentColor"/>
          <rect x="4" y="14" width="16" height="3" fill="currentColor"/>
          <rect x="10" y="8" width="2" height="2" fill="white" opacity="0.7"/>
          <rect x="12" y="11" width="2" height="2" fill="white" opacity="0.5"/>`,
  },

  sneaker: {
    viewBox: '0 0 24 24',
    path: `<path d="M3 12h6l3 3h9v4H3z" fill="currentColor"/>
          <rect x="3" y="8" width="6" height="4" fill="currentColor"/>
          <rect x="3" y="19" width="18" height="2" fill="currentColor" opacity="0.5"/>
          <rect x="13" y="12" width="2" height="2" fill="white" opacity="0.6"/>
          <rect x="16" y="13" width="2" height="2" fill="white" opacity="0.6"/>`,
  },

  wand: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="16" width="14" height="3" fill="currentColor" transform="rotate(-45 4 16)"/>
          <rect x="17" y="2" width="4" height="4" fill="currentColor"/>
          <rect x="14" y="7" width="2" height="2" fill="currentColor" opacity="0.6"/>
          <rect x="20" y="8" width="2" height="2" fill="currentColor" opacity="0.6"/>
          <rect x="15" y="1" width="2" height="2" fill="currentColor" opacity="0.6"/>`,
  },

  clover: {
    viewBox: '0 0 24 24',
    path: `<rect x="4" y="4" width="6" height="6" fill="currentColor"/>
          <rect x="14" y="4" width="6" height="6" fill="currentColor"/>
          <rect x="4" y="12" width="6" height="6" fill="currentColor"/>
          <rect x="14" y="12" width="6" height="6" fill="currentColor"/>
          <rect x="10" y="9" width="4" height="4" fill="currentColor"/>
          <rect x="11" y="18" width="2" height="5" fill="currentColor"/>`,
  },

  helmet: {
    viewBox: '0 0 24 24',
    path: `<path d="M6 12a6 6 0 0 1 12 0v6H6z" fill="currentColor"/>
          <rect x="2" y="4" width="3" height="8" fill="currentColor"/>
          <rect x="19" y="4" width="3" height="8" fill="currentColor"/>
          <rect x="11" y="12" width="2" height="6" fill="white" opacity="0.5"/>`,
  },

  wings: {
    viewBox: '0 0 24 24',
    path: `<path d="M11 12L2 4l3 10 4 4z" fill="currentColor"/>
          <path d="M13 12l9-8-3 10-4 4z" fill="currentColor"/>
          <rect x="11" y="10" width="2" height="10" fill="currentColor" opacity="0.7"/>`,
  },

  jetpack: {
    viewBox: '0 0 24 24',
    path: `<rect x="5" y="4" width="5" height="12" fill="currentColor"/>
          <rect x="14" y="4" width="5" height="12" fill="currentColor"/>
          <rect x="10" y="7" width="4" height="6" fill="currentColor" opacity="0.6"/>
          <rect x="6" y="17" width="3" height="3" fill="currentColor" opacity="0.7"/>
          <rect x="15" y="17" width="3" height="3" fill="currentColor" opacity="0.7"/>
          <rect x="7" y="21" width="1" height="2" fill="currentColor" opacity="0.5"/>
          <rect x="16" y="21" width="1" height="2" fill="currentColor" opacity="0.5"/>`,
  },

  pendant: {
    viewBox: '0 0 24 24',
    path: `<path d="M6 2h12l-2 6H8z" fill="none" stroke="currentColor" stroke-width="2"/>
          <rect x="9" y="10" width="6" height="8" fill="currentColor"/>
          <rect x="11" y="12" width="2" height="4" fill="white" opacity="0.6"/>`,
  },

  moustache: {
    viewBox: '0 0 24 24',
    path: `<path d="M12 10c-2 3-5 3-8 1 1 5 6 5 8 2 2 3 7 3 8-2-3 2-6 2-8-1z" fill="currentColor"/>`,
  },

  headphones: {
    viewBox: '0 0 24 24',
    path: `<path d="M4 14a8 8 0 0 1 16 0" fill="none" stroke="currentColor" stroke-width="3"/>
          <rect x="3" y="13" width="4" height="7" fill="currentColor"/>
          <rect x="17" y="13" width="4" height="7" fill="currentColor"/>`,
  },

  beard: {
    viewBox: '0 0 24 24',
    path: `<path d="M5 6v6a7 7 0 0 0 14 0V6l-3 3-4-3-4 3z" fill="currentColor"/>
          <rect x="9" y="12" width="6" height="3" fill="white" opacity="0.4"/>`,
  },
} satisfies Record<string, PixelIcon>;

export type PixelIconName = keyof typeof pixelIcons;

export function hasPixelIcon(name: string): name is PixelIconName {
  return Object.prototype.hasOwnProperty.call(pixelIcons, name);
}

export { pixelIcons };
export type { PixelIcon };
